/*!
 * a11y-cart-drawer.js — keyboard accessibility fix for the Xtra slide-out cart drawer.
 *
 * Fixes WCAG 2.1 A failures 2.1.1 (Keyboard) and 2.4.3 (Focus Order):
 *   - promotes the drawer's tabindex="-1" controls to focusable while it is open
 *     (defeats 6.2's 500ms re-clobber; harmless no-op on 8.0),
 *   - moves focus into the drawer on open,
 *   - traps Tab focus within the drawer (the piece stock 6.2 AND 8.0 both lack),
 *   - restores focus to the element that opened the drawer on close.
 *
 * Version-agnostic and self-contained — touches no theme internals.
 * Wire up once in layout/theme.liquid before </body>:
 *   <script src="{{ 'a11y-cart-drawer.js' | asset_url }}" defer></script>
 *
 * If a future theme version renames things, update the two constants below.
 */
(function () {
  'use strict';

  var DRAWER_ID = 'cart'; // <aside id="cart"> slide-out cart drawer
  var FOCUSABLE = 'a[href], area[href], button, input, select, textarea, ' +
                  '[role="button"], summary, [tabindex]';

  var existing = document.getElementById(DRAWER_ID);
  if (existing) {
    init(existing);
  } else {
    // Drawer element is injected late — wait for it, then stop observing.
    var boot = new MutationObserver(function (_records, obs) {
      var d = document.getElementById(DRAWER_ID);
      if (d) { obs.disconnect(); init(d); }
    });
    boot.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init(drawer) {
    var lastTrigger = null;
    var isOpen = false;

    function focusables() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (el) {
          return !el.disabled &&
                 el.getAttribute('aria-hidden') !== 'true' &&
                 el.getClientRects().length > 0; // visible
        }
      );
    }

    // Undo any tabindex="-1" on the OPEN drawer's controls. Only mutates
    // elements currently at -1, so it settles after one pass (no observer loop).
    function enable() {
      focusables().forEach(function (el) {
        if (el.getAttribute('tabindex') === '-1') el.setAttribute('tabindex', '0');
      });
    }

    function onKeydown(e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      var active = document.activeElement;

      if (!drawer.contains(active)) {          // focus escaped — pull it back
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // While open, re-assert focusability whenever the theme re-injects the
    // drawer's content or re-applies tabindex="-1" (6.2's 500ms timer).
    var contentObserver = new MutationObserver(function () {
      if (isOpen) enable();
    });

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastTrigger = document.activeElement; // usually the cart icon
      enable();
      contentObserver.observe(drawer, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex']
      });
      document.addEventListener('keydown', onKeydown, true);
      // Move focus into the drawer once content is present.
      setTimeout(function () {
        if (!isOpen) return;
        var f = focusables();
        if (f.length) f[0].focus();
      }, 60);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      contentObserver.disconnect();
      document.removeEventListener('keydown', onKeydown, true);
      if (lastTrigger &&
          typeof lastTrigger.focus === 'function' &&
          document.body.contains(lastTrigger)) {
        lastTrigger.focus();
      }
      lastTrigger = null;
    }

    function opened() {
      return drawer.classList.contains('toggle') ||
             drawer.getAttribute('aria-hidden') === 'false';
    }

    // Keep the focused control on screen. The drawer (#cart / .m6pn) is itself
    // the scroll container (overflow-y:auto), and native focus-scroll can miss
    // controls below the fold (e.g. Checkout with a long cart). block:'nearest'
    // is a no-op when the control is already fully visible, so there's no jitter.
    drawer.addEventListener('focusin', function (e) {
      if (e.target && typeof e.target.scrollIntoView === 'function') {
        e.target.scrollIntoView({ block: 'nearest' });
      }
    });

    // Track the drawer's own open/close state.
    new MutationObserver(function () {
      if (opened()) open(); else close();
    }).observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });

    if (opened()) open(); // in case it is already open at load
  }
})();
